import { CutDay } from "../../../domain/treatmentPlan.interface";

/**
 * Cut days: which slice of the week counts as "the current period".
 *
 * On each running day the missed-entries processor looks up the cut day whose
 * `runningday` is today, then counts visits between `startcutday` and
 * `endcutday`. Get these wrong and nobody is ever flagged for a missed visit —
 * silently, because the processor just logs and returns.
 *
 * Two things make them easy to get wrong by hand, which is why they are derived
 * here and only then offered for editing:
 *
 * 1. The resolver never returns today. It walks back to the most recent
 *    occurrence of a day name, adding a week when that lands on today — so a cut
 *    day whose start and end are both the running day covers a single day a
 *    WEEK in the past, not the week just gone.
 * 2. The windows have to partition the week between the running days, or a
 *    stretch of days belongs to no period at all.
 *
 * The defaults below are the shapes real, working schedules use.
 */

const DEFAULTS: Record<number, CutDay[]> = {
	// One visit a week, checked on Sunday: the whole preceding week.
	1: [{ runningday: "Sun", startcutday: "Sun", endcutday: "Sat" }],
	// Twice weekly, checked on Sunday and Thursday: two halves.
	2: [
		{ runningday: "Sun", startcutday: "Wed", endcutday: "Sat" },
		{ runningday: "Thu", startcutday: "Sun", endcutday: "Wed" },
	],
	// Three times weekly, checked on Wednesday, Friday and Sunday: three slices.
	3: [
		{ runningday: "Wed", startcutday: "Sun", endcutday: "Tue" },
		{ runningday: "Fri", startcutday: "Wed", endcutday: "Thu" },
		{ runningday: "Sun", startcutday: "Fri", endcutday: "Sat" },
	],
};

/** Day names cut days are written with, in week order. */
export const CUT_DAY_NAMES = [
	"Sun",
	"Mon",
	"Tue",
	"Wed",
	"Thu",
	"Fri",
	"Sat",
] as const;

/**
 * A starting set of cut days for a cadence.
 *
 * Returns an empty list for seven-a-week, which never reads cut days — the
 * daily processor works the week out itself — and for anything with no known
 * shape, where a guess would be worse than nothing.
 */
export function deriveCutDays(visitsPerWeek: number): CutDay[] {
	return (DEFAULTS[visitsPerWeek] ?? []).map((cutDay) => ({ ...cutDay }));
}

/** Whether a cadence uses cut days at all. */
export function usesCutDays(visitsPerWeek: number): boolean {
	return visitsPerWeek !== 7 && visitsPerWeek > 0;
}

/**
 * Cut days a cadence has no default for, and which therefore have to be written
 * by hand — worth saying out loud rather than leaving an empty list.
 */
export function hasDerivableCutDays(visitsPerWeek: number): boolean {
	return DEFAULTS[visitsPerWeek] !== undefined;
}

/**
 * The next free option number.
 *
 * Numbering starts at 1 and is assigned rather than typed: option 0 is
 * indistinguishable from "unset" everywhere it is read, and a template pointing
 * at it cannot be saved.
 */
export function nextOptionNumber(
	existing: { cmVisitScheduleOptions?: number }[],
): number {
	const used = existing
		.map((option) => option.cmVisitScheduleOptions ?? 0)
		.filter((value) => Number.isFinite(value));
	return used.length === 0 ? 1 : Math.max(...used, 0) + 1;
}
